import { type ComponentProps } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Label } from '@apps/web/components/ui/label';
import { cn } from '@apps/web/lib/utils';
import { type AccountKanbanCardRead } from '@libraries/typescript/api_client';

export type AccountHealthStatus = AccountKanbanCardRead['health_status'];
export type AccountHealthVerdictSelection =
    | Exclude<AccountHealthStatus, 'neutral'>
    | 'no_change';

export const ACCOUNT_HEALTH_STATUS_CONFIG = {
    red: {
        label: 'Red',
        dotClassName: 'bg-red-500',
        sortRank: 0,
    },
    yellow: {
        label: 'Yellow',
        dotClassName: 'bg-yellow-400',
        sortRank: 1,
    },
    neutral: {
        label: 'Neutral',
        dotClassName: 'border-muted-foreground/50 border bg-transparent',
        sortRank: 2,
    },
    green: {
        label: 'Green',
        dotClassName: 'bg-green-500',
        sortRank: 3,
    },
} as const satisfies Record<
    AccountHealthStatus,
    {
        readonly label: string;
        readonly dotClassName: string;
        readonly sortRank: number;
    }
>;

const ACCOUNT_HEALTH_VERDICT_OPTIONS = [
    {
        value: 'no_change',
        label: 'No change',
    },
    {
        value: 'green',
        label: ACCOUNT_HEALTH_STATUS_CONFIG.green.label,
    },
    {
        value: 'yellow',
        label: ACCOUNT_HEALTH_STATUS_CONFIG.yellow.label,
    },
    {
        value: 'red',
        label: ACCOUNT_HEALTH_STATUS_CONFIG.red.label,
    },
] as const satisfies ReadonlyArray<{
    readonly value: AccountHealthVerdictSelection;
    readonly label: string;
}>;

export function HealthStatusDot({
    status,
    className,
    ...props
}: Readonly<Omit<ComponentProps<'span'>, 'aria-label' | 'children'>> & {
    readonly status: AccountHealthStatus;
}) {
    const config = ACCOUNT_HEALTH_STATUS_CONFIG[status];

    return (
        <span
            {...props}
            role="img"
            aria-label={`${config.label} health status`}
            data-health-status={status}
            className={cn(
                'inline-block size-2.5 shrink-0 rounded-full',
                config.dotClassName,
                className,
            )}
        />
    );
}

export function AccountHealthVerdictPicker({
    labelId,
    selection,
    disabled = false,
    onSelectionChange,
}: {
    readonly labelId: string;
    readonly selection: AccountHealthVerdictSelection;
    readonly disabled?: boolean;
    readonly onSelectionChange: (
        selection: AccountHealthVerdictSelection,
    ) => void;
}) {
    return (
        <div className="flex flex-col gap-2">
            <Label id={labelId}>Account health</Label>
            <div
                role="radiogroup"
                aria-labelledby={labelId}
                className="bg-muted/50 grid grid-cols-4 gap-1 rounded-md border p-1"
            >
                {ACCOUNT_HEALTH_VERDICT_OPTIONS.map((option) => {
                    const isSelected = selection === option.value;

                    return (
                        <Button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            variant="ghost"
                            size="sm"
                            disabled={disabled}
                            className={cn(
                                'h-8 min-w-0 gap-1.5 px-2 text-xs',
                                isSelected &&
                                    'bg-background hover:bg-background shadow-sm',
                            )}
                            onClick={() => onSelectionChange(option.value)}
                        >
                            {option.value === 'no_change' ? (
                                <span
                                    aria-hidden={true}
                                    className="bg-muted-foreground/50 size-2 shrink-0 rounded-full"
                                />
                            ) : (
                                <HealthStatusDot
                                    aria-hidden={true}
                                    status={option.value}
                                    className="size-2"
                                />
                            )}
                            <span className="truncate">{option.label}</span>
                        </Button>
                    );
                })}
            </div>
        </div>
    );
}
